// Spec §2.5 Tab 2 "Map view + stop sequence UI" + Tab 4 "Real-time map"
// reused as a single component. Google Maps (ui/google-map).
//
// - Numbered markers coloured by stop status (matches StopStatusBadge)
// - Auto-fit bounds to all located stops, the depot, live pucks and the trail
// - Marker popup contains "Хүргэлт нээх" link → /supplier-delivery/detail/:id
//
// Live driver markers: DeliveryRouteDetail polls GET /ord/metrics/driver-locations
// (latest GPS per IN_PROGRESS DN) and passes the route-driver's point(s) in via
// driverPucks. `trail` (recordedAt ASC) drives the playback scrubber.

import { ActionIcon, Anchor, Box, Group, Paper, Slider, Stack, Text } from "@mantine/core"
import { ClockIcon, PathIcon, PauseIcon, PlayIcon, TruckIcon, WarehouseIcon } from "@phosphor-icons/react"
import { PolylineF } from "@react-google-maps/api"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { IRouteStop } from "../../apis/delivery-route"
import { dashedLine, HtmlMarker, LatLng, MapCanvas, MapPopup, UB_CENTER, useMapColor } from "../ui/google-map"
import mapClasses from "../ui/google-map/google-map.module.css"

// Indigo ring while open, green once delivered, red/yellow on trouble. The DN status wins:
// stop.status stays PENDING until the route closes (same rule as RouteTable's stop progress).
const DN_TONE: Record<string, string | undefined> = { DELIVERED: "green", CANCELLED: "red", RETURNED: "red" }
const STOP_TONE: Record<string, string | undefined> = { COMPLETED: "green", FAILED: "red", SKIPPED: "yellow" }
const stopTone = (s: IRouteStop) => DN_TONE[s.dn?.deliveryNoteStatus ?? ""] ?? STOP_TONE[s.status ?? ""]

interface DriverPuck {
  deliveryNoteId: string
  driverUserId: string
  latitude: number
  longitude: number
  headingDeg: number | null
  recordedAt: string
}

interface Props {
  stops: IRouteStop[]
  height?: number | string
  // Optional live GPS pucks rendered on top of the stop markers.
  // Each puck = a brand-coloured div-icon with a truck glyph.
  driverPucks?: DriverPuck[]
  // When provided, an overlay card at the bottom-left shows the total
  // route distance + planned duration (matches the Routes-tab mockup).
  distanceKm?: string | number | null
  durationMinutes?: number | null
  // When true, a green warehouse pin is dropped at the first stop so the
  // dispatcher sees the depot origin alongside stop #1.
  showWarehouse?: boolean
  // The route's warehouse (GET /delivery-route/:id `warehouse`). With coordinates the depot pin is
  // drawn there (and joins the bounds); without them it falls back to the first stop (O120).
  warehouse?: { name?: string | null; latitude: number | null; longitude: number | null } | null
  // Full GPS trail (recordedAt ASC) for the playback scrubber. When non-empty,
  // a trail polyline + Play/scrub controls render and the live driverPucks are
  // suppressed (the scrubbed marker supersedes them).
  trail?: DriverPuck[]
}

/** Driver position; the disc turns with the GPS heading when the device reports one. */
function TruckPuck({ heading, tone }: { heading: number | null; tone?: "grape" }) {
  return (
    <Box className={mapClasses.puck} data-tone={tone} style={heading != null ? { transform: `rotate(${heading}deg)` } : undefined}>
      <TruckIcon size={16} weight="fill" />
    </Box>
  )
}

export function RouteMap({
  stops,
  height = 320,
  driverPucks = [],
  distanceKm,
  durationMinutes,
  showWarehouse = false,
  warehouse = null,
  trail = [],
}: Props) {
  const { t } = useTranslation(["delivery"])
  const routeColor = useMapColor("indigo", 7)
  const trailColor = useMapColor("teal", 6)
  // One popup at a time: "stop:<id>", "puck:<dnId>" or "trail".
  const [openKey, setOpenKey] = useState<string | null>(null)
  const warehouseLat = warehouse?.latitude != null ? Number(warehouse.latitude) : NaN
  const warehouseLng = warehouse?.longitude != null ? Number(warehouse.longitude) : NaN
  const warehousePoint = useMemo<LatLng | null>(
    () => (Number.isFinite(warehouseLat) && Number.isFinite(warehouseLng) ? { lat: warehouseLat, lng: warehouseLng } : null),
    [warehouseLat, warehouseLng],
  )
  // Playback state. idx walks the trail; `playing` advances it on a timer.
  const hasTrail = trail.length > 0
  const [idx, setIdx] = useState(0)
  const [playing, setPlaying] = useState(false)

  // Reset whenever the trail identity changes (route switch / refresh).
  const trailSig = hasTrail
    ? `${trail.length}:${trail[0].recordedAt}:${trail[trail.length - 1].recordedAt}`
    : ""
  useEffect(() => {
    setIdx(0)
    setPlaying(false)
  }, [trailSig])

  useEffect(() => {
    if (!playing || !hasTrail) return
    const timer = setInterval(() => {
      setIdx((i) => {
        if (i >= trail.length - 1) {
          setPlaying(false)
          return i
        }
        return i + 1
      })
    }, 400)
    return () => clearInterval(timer)
  }, [playing, hasTrail, trail.length])

  const safeIdx = hasTrail ? Math.min(idx, trail.length - 1) : 0
  const currentPoint = hasTrail ? trail[safeIdx] : null
  const trailLatLngs = useMemo(() => trail.map((p): LatLng => ({ lat: p.latitude, lng: p.longitude })), [trail])
  const togglePlay = () => {
    if (safeIdx >= trail.length - 1) setIdx(0)
    setPlaying((p) => !p)
  }
  // Filter to stops that actually have coordinates; otherwise nothing
  // useful to plot. Sort by sequence so polyline draws in route order.
  // Sequelize returns DECIMAL columns as strings, so coerce + validate
  // rather than relying on typeof number.
  const located = useMemo(
    () =>
      stops
        .map((s) => {
          const lat = s.latitude != null ? Number(s.latitude) : NaN
          const lng = s.longitude != null ? Number(s.longitude) : NaN
          return { ...s, _lat: lat, _lng: lng }
        })
        .filter((s) => Number.isFinite(s._lat) && Number.isFinite(s._lng))
        .sort((a, b) => a.stopNumber - b.stopNumber),
    [stops],
  )

  const polylinePoints = useMemo(() => located.map((s): LatLng => ({ lat: s._lat, lng: s._lng })), [located])
  const center: LatLng = polylinePoints[0] ?? UB_CENTER

  // Stops + driver pucks + GPS trail all contribute to fit-bounds.
  const allPoints = useMemo(
    () => [
      ...polylinePoints,
      ...(warehousePoint ? [warehousePoint] : []),
      ...driverPucks.map((p): LatLng => ({ lat: p.latitude, lng: p.longitude })),
      ...trailLatLngs,
    ],
    [polylinePoints, warehousePoint, driverPucks, trailLatLngs],
  )

  const hasOverlay = distanceKm != null || durationMinutes != null
  const depot = showWarehouse ? (warehousePoint ?? polylinePoints[0] ?? null) : null
  const openStop = located.find((s) => openKey === `stop:${s.id}`)
  const openPuck = hasTrail ? undefined : driverPucks.find((p) => openKey === `puck:${p.deliveryNoteId}`)

  return (
    <Stack gap="xs">
    <Box
      pos="relative"
      style={{
        height,
        borderRadius: "var(--mantine-radius-md)",
        overflow: "hidden",
        border: "1px solid var(--mantine-color-gray-3)",
      }}>
      <MapCanvas height="100%" center={center} zoom={located.length > 0 ? 12 : 11} fitPoints={allPoints}>
        {polylinePoints.length >= 2 && <PolylineF path={polylinePoints} options={dashedLine(routeColor)} />}
        {/* GPS trail (solid teal) — distinct from the dashed stop route */}
        {trailLatLngs.length >= 2 && (
          <PolylineF
            path={trailLatLngs}
            options={{ strokeColor: trailColor, strokeOpacity: 0.65, strokeWeight: 3 }}
          />
        )}
        {depot && (
          <HtmlMarker position={depot} zIndex={1}>
            <Box className={mapClasses.warehouse} title={warehouse?.name ?? undefined}>
              <WarehouseIcon size={16} weight="fill" />
            </Box>
          </HtmlMarker>
        )}
        {located.map((s) => (
          <HtmlMarker
            key={s.id}
            position={{ lat: s._lat, lng: s._lng }}
            zIndex={2}
            onClick={() => setOpenKey(`stop:${s.id}`)}
          >
            <Box className={mapClasses.pin} data-tone={stopTone(s)}>
              {s.stopNumber}
            </Box>
          </HtmlMarker>
        ))}
        {/* Scrubbed playback marker at the current trail point */}
        {currentPoint && (
          <HtmlMarker
            position={{ lat: currentPoint.latitude, lng: currentPoint.longitude }}
            zIndex={3}
            onClick={() => setOpenKey("trail")}
          >
            <TruckPuck heading={currentPoint.headingDeg} tone="grape" />
          </HtmlMarker>
        )}
        {/* Live pucks — suppressed during trail playback (scrub-to-end is "now") */}
        {!hasTrail &&
          driverPucks.map((p) => (
            <HtmlMarker
              key={`puck-${p.deliveryNoteId}`}
              position={{ lat: p.latitude, lng: p.longitude }}
              zIndex={3}
              onClick={() => setOpenKey(`puck:${p.deliveryNoteId}`)}
            >
              <TruckPuck heading={p.headingDeg} />
            </HtmlMarker>
          ))}

        {openStop && (
          <MapPopup position={{ lat: openStop._lat, lng: openStop._lng }} offset={14} onClose={() => setOpenKey(null)}>
            <Text fz="sm">
              <Text span fw={700}>
                #{openStop.stopNumber}
              </Text>{" "}
              {openStop.dn?.receiverBusinessName ?? openStop.address ?? "-"}
            </Text>
            {openStop.dn?.receiverBusinessName && openStop.address && (
              <Text fz="sm" c="dimmed">
                {openStop.address}
              </Text>
            )}
            <Anchor component={Link} to={`/supplier-delivery/detail/${openStop.deliveryNoteId}`} size="sm">
              {t("delivery:route.map.openDelivery")}
            </Anchor>
          </MapPopup>
        )}
        {openKey === "trail" && currentPoint && (
          <MapPopup position={{ lat: currentPoint.latitude, lng: currentPoint.longitude }} offset={15} onClose={() => setOpenKey(null)}>
            <Text fz="sm" fw={700}>
              {t("delivery:route.map.driver")}
            </Text>
            <Text fz="sm" c="dimmed">
              {new Date(currentPoint.recordedAt).toLocaleString()}
            </Text>
          </MapPopup>
        )}
        {openPuck && (
          <MapPopup position={{ lat: openPuck.latitude, lng: openPuck.longitude }} offset={15} onClose={() => setOpenKey(null)}>
            <Text fz="sm" fw={700}>
              {t("delivery:route.map.driver")}
            </Text>
            <Text fz="sm" c="dimmed">
              {t("delivery:route.map.lastSeen", { time: new Date(openPuck.recordedAt).toLocaleTimeString() })}
            </Text>
            <Anchor component={Link} to={`/supplier-delivery/detail/${openPuck.deliveryNoteId}`} size="sm">
              {t("delivery:route.map.openDelivery")}
            </Anchor>
          </MapPopup>
        )}
      </MapCanvas>

      {hasOverlay && (
        <Paper
          pos="absolute"
          bottom={10}
          left={10}
          px={10}
          py={6}
          radius="md"
          shadow="sm"
          style={{
            background: "rgba(255,255,255,0.95)",
            zIndex: 500,
            pointerEvents: "none",
          }}>
          <Group gap={6} wrap="nowrap">
            <PathIcon size={12} color="var(--mantine-color-dark-5)" />
            <Text size="sm">
              {t("delivery:route.map.total")} <Text component="span" fw={700}>{distanceKm ?? "-"} {t("delivery:route.unit.km")}</Text>
            </Text>
            {durationMinutes != null && (
              <>
                <Text size="sm" c="dimmed">·</Text>
                <ClockIcon size={12} color="var(--mantine-color-dark-5)" />
                <Text size="sm">
                  ~<Text component="span" fw={700}>{durationMinutes} {t("delivery:route.unit.min")}</Text>
                </Text>
              </>
            )}
          </Group>
        </Paper>
      )}
    </Box>

      {hasTrail && (
        <Paper withBorder radius="md" p="xs">
          <Group gap="sm" wrap="nowrap" align="center">
            <ActionIcon
              variant="light"
              color="grape"
              size="lg"
              onClick={togglePlay}
              aria-label={playing ? t("delivery:route.map.pause") : t("delivery:route.map.play")}>
              {playing ? <PauseIcon size={16} weight="fill" /> : <PlayIcon size={16} weight="fill" />}
            </ActionIcon>
            <Slider
              flex={1}
              min={0}
              max={Math.max(0, trail.length - 1)}
              value={safeIdx}
              onChange={(v) => {
                setIdx(v)
                setPlaying(false)
              }}
              label={null}
              color="grape"
            />
            <Text size="sm" c="dimmed" miw={140} ta="right" style={{ fontVariantNumeric: "tabular-nums" }}>
              {currentPoint ? new Date(currentPoint.recordedAt).toLocaleString() : "-"}
            </Text>
          </Group>
        </Paper>
      )}
    </Stack>
  )
}
